/**
 * What a node in the system map can BE.
 *
 * ── WHY THIS IS NOT `RoleResourceType` ──────────────────────────────────────
 *
 * `RoleResourceType` (`../roles/role.types.ts`) has five members and is the set
 * of things a Role can own EXCLUSIVELY. It is written into
 * `RoleResource.resourceType`, a real column. The system map draws arrows to
 * things a Role can never own that way — a collection, an external tool, the
 * workspace itself — so widening `RoleResourceType` to cover
 * them would make `RoleResource.resourceType = "collection"` a value the type
 * system permits and the product cannot mean.
 *
 * ⚠️ "NOT OWNABLE" IS TWO DIFFERENT FACTS HERE AND ONLY ONE OF THEM MEANS
 * "A ROLE CANNOT HAVE IT". `collection`, `workspace` and `external_tool` are
 * absent from the ownable set because a Role reaches them through an M:N GRANT
 * table — several Roles hold the same one. `document`, `credential`,
 * `inbox_conversation`, `workflow_execution` and
 * `emulator_session` are absent because no Role can hold them at all. Reading
 * the first group as the second is what makes a reviewer ask why the tool a
 * customer just granted has `roleId: null`.
 *
 * So this is a SEPARATE, WIDER list, and the relationship between the two runs
 * one way only: every `RoleResourceType` is a `RoleSystemNodeType`, and
 * `ROLE_OWNABLE_SYSTEM_NODE_TYPES` below is proven to be exactly that subset at
 * compile time. Nothing here can be written into a `RoleResource` row.
 */
import { z } from "zod";

import { ResourceType, type RoleGrantOnlyResourceType } from "../permissions/permissions.types";
import { RoleResourceType } from "../roles/role.types";

/**
 * Every kind of thing the map can draw.
 *
 * The first five are the Role-ownable resources and their string values are
 * IDENTICAL to `RoleResourceType`'s on purpose — a node's id and type can be
 * joined straight against a `RoleResource` row with no translation table. The
 * three grantable kinds do the same job against their grant tables
 * (`granted-node-names.service.ts` proves that equality at compile time). The
 * rest are reachable and held by nothing.
 */
export const RoleSystemNodeType = {
  // ── Role-ownable (mirrors RoleResourceType exactly) ──
  AGENT: "agent",
  WORKFLOW: "workflow",
  DEPLOYMENT: "deployment",
  AI_TASK: "ai_task",
  DOCUMENT_TEMPLATE: "document_template",

  // ── Grantable: shared across Roles, so held through a grant table ──
  /**
   * A plugin / external tool. NOT ownable, and it was until 2026-08-13: it
   * moved to `RoleExternalToolGrant` because several Roles legitimately hold
   * one catalogue tool and `RoleResource`'s unique key cannot express that.
   */
  EXTERNAL_TOOL: "external_tool",

  // ── Reachable, never ownable ──
  /** A knowledge collection. Read by `searchCollection` and by `collections.*`. */
  COLLECTION: "collection",
  /** A single knowledge document. */
  DOCUMENT: "document",
  /** A stored credential — named by `surfer`, never dereferenced by the map. */
  CREDENTIAL: "credential",
  /** The organisation workspace, as `nexusApi` `workspace.write` names it. */
  WORKSPACE: "workspace",
  /** One inbox conversation. */
  INBOX_CONVERSATION: "inbox_conversation",
  /** One workflow execution record. */
  WORKFLOW_EXECUTION: "workflow_execution",
  /** An emulator test session. */
  EMULATOR_SESSION: "emulator_session"
} as const;

export type RoleSystemNodeType = (typeof RoleSystemNodeType)[keyof typeof RoleSystemNodeType];

export const ROLE_SYSTEM_NODE_TYPES = Object.values(RoleSystemNodeType) as [
  RoleSystemNodeType,
  ...RoleSystemNodeType[]
];

export const RoleSystemNodeTypeSchema = z.enum(ROLE_SYSTEM_NODE_TYPES);

/**
 * The subset a `RoleResource` row can actually hold.
 *
 * Declared by hand and then PROVEN equal to `RoleResourceType` below, rather
 * than derived from it — a derived list would silently absorb a new
 * `RoleResourceType` member without anyone deciding what the map should draw
 * for it, which is the failure this whole module is built to refuse.
 */
export const ROLE_OWNABLE_SYSTEM_NODE_TYPES = [
  RoleSystemNodeType.AGENT,
  RoleSystemNodeType.WORKFLOW,
  RoleSystemNodeType.DEPLOYMENT,
  RoleSystemNodeType.AI_TASK,
  RoleSystemNodeType.DOCUMENT_TEMPLATE
] as const;

export type RoleOwnableSystemNodeType = (typeof ROLE_OWNABLE_SYSTEM_NODE_TYPES)[number];

/**
 * Compile-time proof that the ownable subset and `RoleResourceType` are the
 * same set, in both directions.
 *
 * Add a member to `RoleResourceType` and the SECOND line fails until it is
 * added here; add one here that is not ownable and the FIRST line fails. A
 * one-directional check would pass on a stale list.
 */
type _OwnableIsResourceType = RoleOwnableSystemNodeType extends RoleResourceType ? true : never;
type _ResourceTypeIsOwnable = RoleResourceType extends RoleOwnableSystemNodeType ? true : never;
const _ownableMatchesRoleResourceType: [_OwnableIsResourceType, _ResourceTypeIsOwnable] = [
  true,
  true
];
void _ownableMatchesRoleResourceType;

/** Whether a Role can own this kind of node, so the map knows when `roleId: null` is normal. */
export function isRoleOwnableSystemNodeType(
  type: RoleSystemNodeType
): type is RoleOwnableSystemNodeType {
  return (ROLE_OWNABLE_SYSTEM_NODE_TYPES as readonly RoleSystemNodeType[]).includes(type);
}

/**
 * The kinds whose reach a Role GRANT actually MOVES — the two the console
 * permission path evaluates an allow-list over.
 *
 * ── 🚨 THIS IS NARROWER THAN "GRANTABLE", AND CONFLATING THE TWO SHIPS A FALSE
 *    SENTENCE TO A CUSTOMER ────────────────────────────────────────────────
 *
 * Three kinds are held through a grant table — `collection`, `workspace` and
 * `external_tool` — and only the first two are NARROWED by that grant.
 * `PermissionCheckerService.isNarrowedOutByGrants` evaluates an allow-list over
 * `KNOWLEDGE` and `WORKSPACE` and nothing else; a catalogue tool carries no
 * `ResourcePermission` mapping at all, so a `RoleExternalToolGrant` is an
 * attachment and moves nobody's access. `role-release-effect.ts` says the same
 * thing from the other end: releasing a tool grant is
 * `no-change-not-access-controlled`.
 *
 * Any copy or badge that says "granting this takes it from someone" is
 * therefore TRUE for the two members below and FALSE for `external_tool`, and
 * the two sentences are indistinguishable from inside a component that only
 * knows the node is unownable. That is what this set exists to separate.
 *
 * ── WHY IT IS PROVEN AGAINST `ROLE_GRANT_ONLY_RESOURCE_TYPES` ───────────────
 *
 * That list is the same fact in the PERMISSIONS vocabulary — the resource types
 * whose access is narrowed exclusively by Role grants — and the backend's own
 * `NarrowableResourceType` is built from the same two members. Declared by hand
 * and then proven equal, per this module's rule: a derived list would absorb a
 * third member without anyone deciding what the map should SAY about it, which
 * is the exact failure this docblock exists to refuse.
 *
 * The two vocabularies do not share spellings — `KNOWLEDGE` is `"knowledge"`
 * and its node is `"collection"` — so the bridge is an explicit bijection
 * rather than a subset check, and it is exhaustive in the permissions direction:
 * a third `ROLE_GRANT_ONLY_RESOURCE_TYPES` member fails to compile until
 * somebody names the node kind it draws as.
 */
export const ROLE_NARROWABLE_SYSTEM_NODE_TYPES = [
  RoleSystemNodeType.COLLECTION,
  RoleSystemNodeType.WORKSPACE
] as const;

export type RoleNarrowableSystemNodeType = (typeof ROLE_NARROWABLE_SYSTEM_NODE_TYPES)[number];

const _NARROWABLE_NODE_TYPE_OF_RESOURCE_TYPE = {
  [ResourceType.KNOWLEDGE]: RoleSystemNodeType.COLLECTION,
  [ResourceType.WORKSPACE]: RoleSystemNodeType.WORKSPACE
} as const satisfies Record<RoleGrantOnlyResourceType, RoleSystemNodeType>;

type _MappedNarrowableNodeType =
  (typeof _NARROWABLE_NODE_TYPE_OF_RESOURCE_TYPE)[RoleGrantOnlyResourceType];
type _NarrowableIsMapped = RoleNarrowableSystemNodeType extends _MappedNarrowableNodeType
  ? true
  : never;
type _MappedIsNarrowable = _MappedNarrowableNodeType extends RoleNarrowableSystemNodeType
  ? true
  : never;
const _narrowableMatchesRoleGrantOnly: [_NarrowableIsMapped, _MappedIsNarrowable] = [true, true];
void _narrowableMatchesRoleGrantOnly;

/**
 * Whether a Role grant on this kind MOVES access, so a caller knows when a
 * sentence about losing or gaining reach may be said at all.
 *
 * ⚠️ Not the complement of {@link isRoleOwnableSystemNodeType}. `external_tool`
 * is neither ownable nor narrowable, and a caller that treats "unownable" as
 * "narrowed by a grant" states the false half of this module's own warning.
 */
export function isRoleNarrowableSystemNodeType(
  type: RoleSystemNodeType
): type is RoleNarrowableSystemNodeType {
  return (ROLE_NARROWABLE_SYSTEM_NODE_TYPES as readonly RoleSystemNodeType[]).includes(type);
}

/**
 * The kinds a caller can rename and re-describe with ONE partial write.
 *
 * ── THIS IS A CLAIM ABOUT ROUTES, AND EACH MEMBER NAMES THE ROUTE IT RESTS ON ─
 *
 * A node kind is in this set when a route exists that accepts a name and/or a
 * description ON THEIR OWN, without resubmitting the rest of the resource:
 *
 *   · `workflow`           `PATCH /api/workflow/:id/details`
 *   · `deployment`         `PUT   /api/deployments/:id`
 *   · `document_template`  `PUT   /api/document-templates/:id`
 *   · `ai_task`            `PATCH /api/tools/ai-tasks/:taskId/details`
 *
 * ── WHY IT IS A SUBSET OF THE OWNABLE SET RATHER THAN A SLICE OF ALL FOURTEEN ─
 *
 * The kinds with no such route are excluded for the ordinary reason. TWO are
 * excluded for a reason that is about their SHAPE instead, and it is the reason
 * a caller must not "fix" this by adding them:
 *
 *   · `agent` has `first_name` + `last_name` and NO description. A name-and-
 *     description form cannot express it, and folding two columns into one box
 *     is a write that loses data.
 *   · `external_tool` carries a PUBLIC name and description alongside a PRIVATE
 *     pair. One box would have to pick one of the two silently. ⚠️ It is also
 *     no longer Role-OWNABLE (it moved to `RoleExternalToolGrant`), so the
 *     subset proof below no longer has anything to say about it — the shape
 *     argument is now the ONLY thing keeping it out, and deleting this bullet
 *     would leave the refusal undocumented rather than merely unproven.
 *
 * So the honest surface for those two is ABSENT, never disabled — a disabled
 * control claims the feature exists and the reader lacks a permission, which is
 * false in both cases.
 *
 * ── THE BOUNDS ARE NOT UNIFORM AND ARE NOT HERE ──────────────────────────────
 *
 * The four routes disagree about the minimum name length, both maxima, whether
 * the name is trimmed before it is measured, and whether an empty patch is
 * refused. Only `workflow`'s bounds live in this package
 * (`../workflow/workflow-details.types.ts`); the other three are literals inside
 * their own schema files. A consumer that needs them therefore reads each
 * schema, and an exhaustive `Record<RenamableSystemNodeType, …>` is what makes a
 * fifth member a compile error rather than a silently unhandled kind.
 */
export const RENAMABLE_SYSTEM_NODE_TYPES = [
  RoleSystemNodeType.WORKFLOW,
  RoleSystemNodeType.DEPLOYMENT,
  RoleSystemNodeType.DOCUMENT_TEMPLATE,
  RoleSystemNodeType.AI_TASK
] as const;

export type RenamableSystemNodeType = (typeof RENAMABLE_SYSTEM_NODE_TYPES)[number];

/**
 * Compile-time proof that every renamable kind is also Role-ownable.
 *
 * ONE direction only, and deliberately: the reverse is FALSE and is the whole
 * design — `agent` is ownable and not renamable. A bidirectional proof like
 * `_ownableMatchesRoleResourceType` above would be wrong here, and writing one
 * would force someone to "fix" it by adding the kind whose shape the docblock
 * explains cannot be expressed.
 */
type _RenamableIsOwnable = RenamableSystemNodeType extends RoleOwnableSystemNodeType ? true : never;
const _renamableIsSubsetOfOwnable: _RenamableIsOwnable = true;
void _renamableIsSubsetOfOwnable;

/** Whether one partial write can change this kind's name and description. */
export function isRenamableSystemNodeType(
  type: RoleSystemNodeType
): type is RenamableSystemNodeType {
  return (RENAMABLE_SYSTEM_NODE_TYPES as readonly RoleSystemNodeType[]).includes(type);
}
